import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "./SimpleSentenceVoiceRecognition.module.css";
import MicrophoneComponent from "../../components/MicrophoneVoiceRecognition/MicrophoneView";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle, faVolumeUp, faPause } from "@fortawesome/free-solid-svg-icons";
import LessonInstruction from "../../containers/instruction/LessonInstruction";
import { createMetrics, getMetrics, updateMetrics } from "../../service/service";
import { useAppGlobalConfig } from "../../providers/AppGlobalConfigContext";
import useAudioPlayback from "../../hooks/useAudioPlayback";

const SimpleSentenceVr = ({
  data,
  lessonName,
  contentIndex,
  callbackExercisesRecordingAttempts,
  callbackVRError,
  step,
  audioUrl,
  blockIndex,
  blockData,
  totalIndexes,
  lessonId
}) => {
  const [recordingAttempts, setRecordingAttempts] = useState(0);
  const [responseDataScore, setResponseDataScore] = useState(null);
  const [valueContentIndex, setValueContentIndex] = useState(0);
  const [letSkip, setLetSkip] = useState(false);
  const [showVRErrorDialogue, setShowVRErrorDialogue] = useState(false);
  const audioRefOne = useRef();
  const audioRefTwo = useRef();
  const [metrics, setMetrics] = useState(null)
  const userInfo = JSON.parse(localStorage.getItem("user-info"));
  const { isEmbedMode } = useAppGlobalConfig();
  let tenant = isEmbedMode ? 'cupheadless' : 'cupfranchise'

  const { state, togglePlayback} = useAudioPlayback(audioUrl, "simple_sentence_vr");

  const urlBucketAudio = `${process.env.REACT_APP_CLOUDFRONT_DNS}/media/audios`;

  useEffect(() => {
    callbackVRError(letSkip);
  }, [letSkip]);
  const getColorClass = (qualityScore) => {
    if (qualityScore <= 59) {
      // cor vermelho.
      return "#dc2626";
    } else if (qualityScore <= 79) {
      // cor laranja.
      return "#ea580c";
    } else {
      // cor verde.
      return "#16a34a";
    }
  };

  useEffect(() => {
    getMetricsData();
  }, [blockIndex, contentIndex]);

  const getMetricsData = useCallback(async () => {
    try {
      const resp = await getMetrics(userInfo.id, blockData.block_id, tenant)
      if (resp.data) {
        setMetrics(resp.data)
      }
    } catch (error) {
      if (error && error.response && error.response.status === 404) {
        setMetrics(null)
      } else {
        console.error("Erro ao buscar métricas:", error);
      }
    }
  }, [blockIndex, contentIndex]);

  const getScoreMessage = (overallScore) => {
    if (overallScore <= 59) {
      return "Your pronunciation is off.";
    } else if (overallScore <= 79) {
      return "You could use some practice.";
    } else {
      return "You got it!";
    }
  };

  const onPlayClick = () => {
    try {
      togglePlayback();
    } catch (error) {
      console.error('Erro ao carregar o áudio:', error.message);
    }
}

  const registerProgress = async (params) => {
    if (metrics) {
      await updateMetrics(params, tenant)
    } else {
      const result = await createMetrics(params, tenant);      
      if (result && result.status===201) {
        setMetrics(true);
      }
    }
  }

  const showContent = (type) => {
    if (data && data.type) {
      switch (type) {
        case "instruction":
          return (
            <>
              <LessonInstruction
                instructionText={data.instruction}
                audioUrl={audioUrl}
              />
            </>
          );

        default:
          return (
            <>
              <div>
                {!showVRErrorDialogue &&
                  <>
                    <div className={styles.block_phrase}>
                      <div>
                        <button
                          className={styles.audio_button}
                          onClick={() => {
                            onPlayClick();
                          }}
                        >
                          <audio
                            ref={audioRefOne}
                            src={`${urlBucketAudio}/${data.sound}`}
                          />
                          <div className={styles.audio_icon_container}>
                            {state === 'playing' ? (
                              <FontAwesomeIcon className={styles.audio_icon} size="2x" icon={faPause} />
                            ) : (
                              <FontAwesomeIcon className={styles.audio_icon} size="2x" icon={faVolumeUp} />
                            )}
                          </div>
                        </button>
                      </div>
                      <h3 className={styles.phrase}>
                        {data.pt.length > 0 && data.pt !== "#" ? data.pt : data.en}
                      </h3>
                    </div>

                    <div className={styles.block_actions}>
                      {data.type === "drill" && (
                        <MicrophoneComponent
                          data={data}
                          blockData={blockData}
                          lessonId={lessonId}
                          lessonName={lessonName}
                          contentIndex={contentIndex}
                          totalIndexes={totalIndexes}
                          template={"simple_sentence_vr"}
                          callbackResponseScore={setResponseDataScore}
                          callbackExercisesRecordingAttempts={
                            callbackExercisesRecordingAttempts
                          }
                          callbackAudioVrRecordingAttempts={setRecordingAttempts}
                          callbackAudioVrSetValueContentIndex={setValueContentIndex}
                          callbackAudioVrSetRecordingAttempts={setRecordingAttempts}
                          callbackRegisterMetrics={registerProgress}
                          step={step}
                          setLetSkip={setLetSkip}
                          setShowVRErrorDialogue={setShowVRErrorDialogue}
                        />
                      )}
                    </div>

                    {contentIndex === valueContentIndex &&
                      responseDataScore &&
                      data.en === responseDataScore.data.text && (
                        <div>
                          <div>
                            <div>
                              <audio
                                ref={audioRefTwo}
                                src={`${urlBucketAudio}/${data.sound}`}
                              />
                            </div>
                            <div className={styles.match_phrase_feedback}>
                              {responseDataScore.data.result.detailed.words.map(
                                (word, index) => (
                                  <span
                                    key={index}
                                    style={{
                                      color: `${getColorClass(word.quality_score)}`,
                                    }}
                                  >
                                    {word.word}{" "}
                                  </span>
                                )
                              )}
                            </div>
                          </div>

                          <div className={styles.feedback_phrase}>
                            <span>
                              {`${responseDataScore.data.result.overall_score
                                }% ${getScoreMessage(
                                  responseDataScore.data.result.overall_score
                                )}`}
                            </span>
                          </div>
                        </div>
                      )}

                    {contentIndex === valueContentIndex && (
                      <div className={styles.feedback}>
                        <span>{recordingAttempts} de 3 tentativas</span>
                      </div>
                    )}
                  </>
                }

                {showVRErrorDialogue && (
                  <div className={styles.feedback}>
                    <div className={styles.error_message_container}>
                      <FontAwesomeIcon icon={faExclamationCircle} size={"2x"} style={{ color: "red" }} />
                      <h3 className={styles.error_message}>
                        O serviço de reconhecimento de voz parece estar offline
                      </h3>
                    </div>
                    <div className={styles.buttons_container}>
                      <button
                        className={styles.retry_button}
                        onClick={() => setShowVRErrorDialogue(false)}
                      >
                        Tentar novamente
                      </button>
                      <button
                        className={styles.skip_button}
                        onClick={() => step("forward", true)}
                      >
                        Pular exercício
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          );
      }
    }
  };

  return (
    <>
      <section className={styles.lesson_container}>
        {data && data.type ? (
          showContent(data.type)
        ) : (
          <div>Template não configurado</div>
        )}
      </section>
    </>
  );
};

export default SimpleSentenceVr;